/**
 * Speed and ETA for a transfer row.
 *
 * Pure, and its own module so the numbers can be tested without a store or a
 * webview — the arithmetic is the whole feature, and "it renders something" is
 * not the property worth pinning.
 */

/** `1.4 MB/s`. Null when there is no usable rate yet, so the caller renders
 *  nothing rather than "0 B/s", which reads as "stalled" during the first tick
 *  of a perfectly healthy transfer. */
export function formatRate(bps, formatBytes) {
  if (!Number.isFinite(bps) || bps <= 0) return null;
  return `${formatBytes(Math.round(bps))}/s`;
}

/**
 * Seconds remaining, or null when it cannot be known: no rate yet, no total
 * (some FTP servers never report a size), or already finished.
 *
 * **Null rather than a guess.** An ETA invented from a missing total is worse
 * than no ETA — the user believes it.
 */
export function etaSeconds(transferred, total, bps) {
  if (!Number.isFinite(bps) || bps <= 0) return null;
  if (!Number.isFinite(total) || total <= 0) return null;
  const left = total - transferred;
  if (!Number.isFinite(left) || left <= 0) return null;
  return left / bps;
}

/**
 * `12s`, `3m 05s`, `1h 04m`. Deliberately coarse above a minute: a
 * second-accurate ETA on a ten-minute upload is false precision, and it
 * flickers.
 */
export function formatEta(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}
